import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import Select from "react-select";

import Label from "@components/form/Label";
import Modal from "@components/layout/Modal";
import TextInput from "@components/form/TextInput";
import ArrowRightCalendarIcon from "@components/icons/ArrowRightCalendarIcon";
import { useReportScopes } from "@hooks/query/useReportScopes";

import EditIcon from "@components/icons/EditIcon";
import { useAppContext } from "@context/AppContext";
import {
  ReportScope,
  ReportRunResult,
  runGeneralReport,
  runReportTemplate,
} from "@services/reportEngine";
import { useCreateReportTemplate } from "@hooks/mutation/useCreateReportTemplate";
import { useUpdateReportTemplate } from "@hooks/mutation/useUpdateReportTemplate";
import { useCreateReportSnapshot } from "@hooks/mutation/useCreateReportSnapshot";
import { toastError, toastSuccess } from "@utils/toast";
import { formatReportPeriodDate, formatReportFilterValue } from "./formatCell";
import { DATE_OPERATOR_LABEL_KEYS, OPERATOR_LABEL_KEYS } from "./StepFilters";
import ReportResultView from "./ReportResultView";
import StepFooter from "./StepFooter";
import { useExportReportRows } from "./useExportReportRows";
import { useAdhocReport } from "@context/AdhocReportContext";

// Match DEQ and the existing SEQwater General Report result table.
const PAGE_SIZE = 100;
type ReportAccountingPeriod = ReportScope["accountingPeriods"][number];
const EMPTY_ACCOUNTING_PERIODS: ReportAccountingPeriod[] = [];
const SAVED_REPORT_NAME_MAX_LENGTH = 255;

type DateRangeOption = {
  label: string;
  value: string;
  from: string;
  to: string;
  accountingPeriodId: string;
};

const dateOnly = (value: string | Date): string => {
  if (typeof value === "string") return value.slice(0, 10);
  return [
    value.getFullYear(),
    String(value.getMonth() + 1).padStart(2, "0"),
    String(value.getDate()).padStart(2, "0"),
  ].join("-");
};

const todayDateOnly = () => dateOnly(new Date());

const shiftDateOnly = (value: string, months: number, days = 0) => {
  const [year, month, day] = value.split("-").map(Number);
  const shifted = new Date(Date.UTC(year, month - 1 + months, day + days));
  return shifted.toISOString().slice(0, 10);
};

const buildSnapshotName = (
  reportName: string,
  scopeName: string,
  from: string,
  to: string,
) => {
  const separator = " — ";
  const dateRange = `${from}–${to}`;
  if (!scopeName) {
    const nameBudget =
      SAVED_REPORT_NAME_MAX_LENGTH - separator.length - dateRange.length;
    return `${reportName.slice(0, nameBudget).trim()}${separator}${dateRange}`;
  }
  const fixedLength = separator.length * 2 + dateRange.length;
  const textBudget = SAVED_REPORT_NAME_MAX_LENGTH - fixedLength;

  // Both names may be 255 characters in source data. Keep the date range and
  // both identities useful instead of letting the API reject the default.
  const reportBudget = Math.min(
    reportName.length,
    Math.max(1, Math.floor(textBudget / 2)),
  );
  const scopeBudget = Math.max(1, textBudget - reportBudget);

  return `${reportName.slice(0, reportBudget).trim()}${separator}${scopeName
    .slice(0, scopeBudget)
    .trim()}${separator}${dateRange}`;
};

/**
 * The run step, working the way the Virginia DEQ one works: the report runs
 * as soon as the step opens, date edits apply on Apply Filter and re-run it,
 * export offers CSV or PDF of selected rows or the whole report, and a run opened from a
 * template is read-only apart from the dates — its filters show as chips.
 */
const StepPreview: React.FunctionComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { checkPermissions } = useAppContext();
  const queryClient = useQueryClient();
  const canGenerate = checkPermissions(["GenerateReports"]);
  const {
    templateId,
    templateName,
    isTemplateRun,
    selectedLevels,
    selectedFields,
    filters,
    fieldByName,
    accountingPeriodId,
    setAccountingPeriodId,
    fromDate,
    toDate,
    setFromDate,
    setToDate,
    announcedAllocations,
    setAnnouncedAllocations,
    goToStep,
    editColumns,
    rememberSavedTemplate,
  } = useAdhocReport();
  const exportBaseName =
    templateName ?? (t("reporting.ad_hoc_report") as string);

  // Dates are staged locally and committed by Apply Filter, as in DEQ; every
  // other input applies immediately. The committed values drive the query.
  const [localFromDate, setLocalFromDate] = useState("");
  const [localToDate, setLocalToDate] = useState("");
  const [selectedRecordIds, setSelectedRecordIds] = useState<any[]>([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [snapshotModalOpen, setSnapshotModalOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [initialisedScopeKey, setInitialisedScopeKey] = useState("all");

  const isAnnouncements = selectedLevels.includes("allocation_announcements");

  // The catalogue owns date semantics. Filters count as reading a field even
  // when it is not projected, because the planner still builds its measure.
  const readsAccountingPeriod = [
    ...selectedFields,
    ...filters.map(filter => filter.field),
  ].some(
    name => fieldByName.get(name)?.periodSemantics === "accounting_period",
  );

  const {
    data: schemes = [],
    isLoading: schemesLoading,
    isError: schemesError,
    refetch: refetchSchemes,
  } = useReportScopes();
  const schemeField = isAnnouncements
    ? "allocationLevel1ResourceName"
    : "level1ResourceName";
  const schemeFilter = filters.find(
    filter => filter.field === schemeField && filter.operator === "eq",
  );
  // An exact scheme filter also identifies the water year. Other comparisons
  // remain ordinary filters and do not imply a single scheme.
  const effectiveSchemeId = schemeFilter
    ? schemes.find(scheme => scheme.name === schemeFilter.value)?.id
    : schemes.length === 1
    ? schemes[0].id
    : undefined;
  const scopeKey = effectiveSchemeId ?? "all";

  const accountingPeriods =
    schemes.find(scheme => scheme.id === effectiveSchemeId)
      ?.accountingPeriods ?? EMPTY_ACCOUNTING_PERIODS;

  const quickDateRanges = useMemo<DateRangeOption[]>(
    () =>
      accountingPeriods.flatMap(period => {
        const periodStart = dateOnly(period.periodStart);
        const periodEnd = dateOnly(period.periodEnd);
        const quarters = Array.from({ length: 4 }, (_, index) => {
          const from = shiftDateOnly(periodStart, index * 3);
          const calculatedTo = shiftDateOnly(periodStart, (index + 1) * 3, -1);
          const to = calculatedTo < periodEnd ? calculatedTo : periodEnd;
          return {
            label: `Q${index + 1} (${formatReportPeriodDate(
              from,
            )} - ${formatReportPeriodDate(to)})`,
            value: `${period.id}:q${index + 1}`,
            from,
            to,
            accountingPeriodId: period.id,
          };
        }).filter(option => option.from <= periodEnd);

        return [
          ...quarters,
          {
            label: `${t("reporting.full_water_year")} (${formatReportPeriodDate(
              periodStart,
            )} - ${formatReportPeriodDate(periodEnd)})`,
            value: `${period.id}:water-year`,
            from: periodStart,
            to: periodEnd,
            accountingPeriodId: period.id,
          },
        ];
      }),
    [accountingPeriods, t],
  );

  const applyAccountingPeriod = useCallback(
    (period: ReportAccountingPeriod) => {
      const start = dateOnly(period.periodStart);
      const end = dateOnly(period.periodEnd);
      const today = todayDateOnly();
      setAccountingPeriodId(period.id);
      setFromDate(start);
      setToDate(today >= start && today <= end ? today : end);
      setLocalFromDate("");
      setLocalToDate("");
    },
    [setAccountingPeriodId, setFromDate, setToDate],
  );

  useEffect(() => {
    // Do not mistake the initial empty query value for a tenant with no
    // schemes. That would briefly run an accounting-period report without
    // its required scope and show a false validation error while schemes are
    // still loading.
    if (schemesLoading) return;
    if (!effectiveSchemeId) {
      setAccountingPeriodId(undefined);
      // Applied dates live in the builder context. Returning from editing
      // columns or filters must not replace them with the default period.
      setInitialisedScopeKey("all");
      return;
    }
    if (!readsAccountingPeriod) {
      setAccountingPeriodId(undefined);
      setInitialisedScopeKey(effectiveSchemeId);
      return;
    }
    if (accountingPeriods.some(period => period.id === accountingPeriodId)) {
      setInitialisedScopeKey(effectiveSchemeId);
      return;
    }

    const today = todayDateOnly();
    const initialPeriod =
      accountingPeriods.find(period => {
        const start = dateOnly(period.periodStart);
        const end = dateOnly(period.periodEnd);
        return start <= today && today <= end;
      }) ??
      accountingPeriods.find(period => period.isActive) ??
      accountingPeriods[0];

    if (initialPeriod) {
      applyAccountingPeriod(initialPeriod);
    } else {
      setAccountingPeriodId(undefined);
    }
    setInitialisedScopeKey(effectiveSchemeId);
  }, [
    effectiveSchemeId,
    readsAccountingPeriod,
    schemesLoading,
    accountingPeriods,
    accountingPeriodId,
    applyAccountingPeriod,
    setAccountingPeriodId,
    setFromDate,
    setToDate,
  ]);

  useEffect(() => {
    if (!readsAccountingPeriod || !accountingPeriodId) return;
    const selectedPeriod = accountingPeriods.find(
      period => period.id === accountingPeriodId,
    );
    if (!selectedPeriod) return;
    const start = dateOnly(selectedPeriod.periodStart);
    const end = dateOnly(selectedPeriod.periodEnd);
    // A user may add an accounting-period field after previously running an
    // unrestricted range. Bring that now-incompatible range back to the
    // selected scheme period before the query is enabled.
    if (fromDate < start || toDate > end) applyAccountingPeriod(selectedPeriod);
  }, [
    readsAccountingPeriod,
    accountingPeriodId,
    accountingPeriods,
    fromDate,
    toDate,
    applyAccountingPeriod,
  ]);

  const definition = useMemo(
    () => ({
      dataLevels: selectedLevels,
      // An all-scheme result must identify the scheme on every row even when
      // the operator did not manually pick the column.
      fields: (() => {
        const scopeField = isAnnouncements
          ? "allocationLevel1ResourceName"
          : "level1ResourceName";
        return !effectiveSchemeId && !selectedFields.includes(scopeField)
          ? [scopeField, ...selectedFields]
          : selectedFields;
      })(),
      filters,
      level1ResourceId: effectiveSchemeId,
      accountingPeriodId: readsAccountingPeriod
        ? accountingPeriodId
        : undefined,
      dateRange: { from: fromDate, to: toDate },
      announcedAllocations: isAnnouncements ? announcedAllocations : undefined,
    }),
    [
      selectedLevels,
      selectedFields,
      filters,
      isAnnouncements,
      effectiveSchemeId,
      readsAccountingPeriod,
      accountingPeriodId,
      fromDate,
      toDate,
      announcedAllocations,
    ],
  );

  const selectedAccountingPeriod = accountingPeriods.find(
    period => period.id === accountingPeriodId,
  );
  const dateRangeFitsAccountingPeriod =
    !readsAccountingPeriod ||
    (selectedAccountingPeriod &&
      fromDate >= dateOnly(selectedAccountingPeriod.periodStart) &&
      toDate <= dateOnly(selectedAccountingPeriod.periodEnd));
  const selectedPeriodEnd = selectedAccountingPeriod
    ? dateOnly(selectedAccountingPeriod.periodEnd)
    : undefined;
  const maximumToDate = readsAccountingPeriod ? selectedPeriodEnd : undefined;
  const needsScheme = readsAccountingPeriod && !effectiveSchemeId;
  const needsAccountingPeriod =
    readsAccountingPeriod && Boolean(effectiveSchemeId) && !accountingPeriodId;
  const scopeInitialised = initialisedScopeKey === scopeKey;
  const metadataError = schemesError;
  // The table is hidden behind the scheme prompt in this state. Nothing may
  // export or snapshot a prior run while the current scope is incomplete.
  const resultHidden =
    needsScheme || needsAccountingPeriod || !scopeInitialised || metadataError;
  const canRun =
    !schemesLoading &&
    !metadataError &&
    scopeInitialised &&
    fromDate !== "" &&
    toDate !== "" &&
    selectedFields.length > 0 &&
    !needsScheme &&
    !needsAccountingPeriod &&
    Boolean(dateRangeFitsAccountingPeriod);

  const applyDates = () => {
    const from = localFromDate || fromDate;
    const to = localToDate || toDate;
    if (from && to && new Date(to) < new Date(from)) {
      toastError(t("reporting.to_date_before_from"));
      return;
    }
    if (
      (new Date(to).getTime() - new Date(from).getTime()) / 86_400_000 >
      365
    ) {
      toastError(t("reporting.date_range_exceeds_one_year"));
      return;
    }
    if (readsAccountingPeriod && selectedAccountingPeriod) {
      const periodStart = dateOnly(selectedAccountingPeriod.periodStart);
      const periodEnd = dateOnly(selectedAccountingPeriod.periodEnd);
      if (from < periodStart || to > periodEnd) {
        toastError(
          t("reporting.date_outside_accounting_period", {
            from: formatReportPeriodDate(periodStart),
            to: formatReportPeriodDate(periodEnd),
          }),
        );
        return;
      }
    }
    if (from === fromDate && to === toDate) refetchRun();
    setFromDate(from);
    setToDate(to);
    setLocalFromDate("");
    setLocalToDate("");
  };

  const {
    data: createdRun,
    isFetching: isCreatingRun,
    isError: runError,
    error: runFailure,
    refetch: refetchRun,
  } = useQuery<ReportRunResult>(
    ["reports", "adhoc", "run", templateId, isTemplateRun, definition],
    () =>
      // DEQ's report contract: one complete result, then client pagination.
      isTemplateRun && templateId
        ? runReportTemplate(templateId, {
            level1ResourceId: definition.level1ResourceId,
            accountingPeriodId: definition.accountingPeriodId,
            dateRange: definition.dateRange,
            announcedAllocations: definition.announcedAllocations,
          })
        : runGeneralReport(definition),
    {
      enabled: canRun,
      keepPreviousData: false,
      refetchOnWindowFocus: false,
      // A run writes audit metadata. Network recovery must not create another
      // run unless the operator explicitly applies the dates or reopens it.
      refetchOnReconnect: false,
      retry: false,
      onError: (error: any) =>
        toastError(
          // The engine explains what it refused and what to change; a generic
          // failure toast throws that away.
          error?.response?.data?.message ?? t("reporting.run_failed"),
        ),
      onSuccess: () => {
        setSelectedRecordIds([]);
        if (templateId) queryClient.invalidateQueries(["reportTemplates"]);
      },
    },
  );

  const result = createdRun;
  const rows = useMemo(() => result?.rows ?? [], [result?.rows]);
  const isFetching = isCreatingRun;

  const handleSelectionChange = useCallback(
    (ids: any[]) => setSelectedRecordIds(ids.map(Number)),
    [],
  );

  const selectedRows = useMemo(() => {
    const selectedIds = new Set(selectedRecordIds.map(Number));
    return rows.filter(row => selectedIds.has(Number(row._rowId)));
  }, [rows, selectedRecordIds]);

  // Named and dated from the result, never from the pickers: a failed re-run
  // leaves the previous rows on screen, and they must not be filed under the
  // period the operator has since typed.
  const resultScopeName = result?.level1ResourceId
    ? result.level1ResourceName ??
      schemes.find(scheme => scheme.id === result.level1ResourceId)?.name
    : (t("reporting.all_schemes") as string);
  const suggestedReportName = result?.dateRange
    ? buildSnapshotName(
        exportBaseName,
        result.level1ResourceId ? resultScopeName ?? "" : "",
        formatReportPeriodDate(result.dateRange.from),
        formatReportPeriodDate(result.dateRange.to),
      )
    : exportBaseName;
  const { exportDialog, openExportDialog, isExporting } = useExportReportRows({
    baseFileName: suggestedReportName,
    columns: result?.columns ?? [],
    rows,
    selectedRows,
    resultToken: result?.resultToken,
  });

  const createTemplate = useCreateReportTemplate();
  const updateTemplate = useUpdateReportTemplate();
  const createSnapshot = useCreateReportSnapshot();

  const handleSaveTemplate = async () => {
    const payload = {
      name: saveName.trim(),
      dataLevels: selectedLevels,
      fields: selectedFields,
      filters,
    };
    try {
      // Keep the saved identity: the completion step names it, and saving
      // again updates in place rather than leaving a duplicate.
      const saved = templateId
        ? await updateTemplate.mutateAsync({ id: templateId, ...payload })
        : await createTemplate.mutateAsync(payload);
      rememberSavedTemplate({ id: saved.id, name: saved.name });
      toastSuccess(t("reporting.template_saved"));
      setTemplateModalOpen(false);
      goToStep(3);
    } catch (error: any) {
      toastError(
        error?.response?.data?.message ?? t("reporting.template_save_failed"),
      );
    }
  };

  const handleSaveSnapshot = async () => {
    try {
      await createSnapshot.mutateAsync({
        name: saveName.trim(),
        resultToken: result!.resultToken,
        // Match DEQ: persist the complete result only on explicit Save.
        rows: result!.rows,
      });
      toastSuccess(t("reporting.snapshot_saved"));
      setSnapshotModalOpen(false);
      // As in DEQ: saving lands you on Report history, where it now lives.
      navigate("/polestar/reports/adhoc#1");
    } catch (error: any) {
      toastError(
        error?.response?.data?.message ?? t("reporting.snapshot_save_failed"),
      );
    }
  };

  return (
    <>
      <ReportResultView
        title={isTemplateRun ? undefined : t("reporting.general_step4_title")}
        subtitle={
          isTemplateRun ? undefined : t("reporting.adhoc_preview_subtitle")
        }
        controls={
          <fieldset className="flex flex-wrap items-end gap-4 border-b border-gray-200 pb-4">
            {effectiveSchemeId && quickDateRanges.length > 0 && (
              <div className="min-w-[17rem] flex-1 basis-72">
                <Label htmlFor="quickDateRange">
                  {t("reporting.quick_date_range")}
                </Label>
                <Select
                  inputId="quickDateRange"
                  options={quickDateRanges}
                  placeholder={t("reporting.custom_date_range")}
                  value={
                    quickDateRanges.find(
                      option =>
                        option.from === (localFromDate || fromDate) &&
                        option.to === (localToDate || toDate),
                    ) ?? null
                  }
                  onChange={(option: DateRangeOption | null) => {
                    if (!option) return;
                    if (readsAccountingPeriod) {
                      setAccountingPeriodId(option.accountingPeriodId);
                    }
                    setFromDate(option.from);
                    setToDate(option.to);
                    setLocalFromDate("");
                    setLocalToDate("");
                  }}
                />
              </div>
            )}
            <div className="w-full min-w-0 flex-[2_1_28rem] sm:min-w-[22rem]">
              <Label htmlFor="from-date">{t("reporting.filter_dates")}</Label>
              <div className="grid min-w-0 grid-cols-[minmax(0,1fr)_2.5rem_minmax(0,1fr)] items-center gap-1">
                <TextInput
                  id="from-date"
                  aria-label={t("common.from") as string}
                  type="date"
                  value={localFromDate || fromDate}
                  min={
                    readsAccountingPeriod && accountingPeriodId
                      ? dateOnly(
                          accountingPeriods.find(
                            period => period.id === accountingPeriodId,
                          )?.periodStart ?? "",
                        ) || undefined
                      : undefined
                  }
                  max={
                    readsAccountingPeriod && accountingPeriodId
                      ? dateOnly(
                          accountingPeriods.find(
                            period => period.id === accountingPeriodId,
                          )?.periodEnd ?? "",
                        ) || undefined
                      : undefined
                  }
                  onChange={e => setLocalFromDate(e.target.value)}
                />
                <ArrowRightCalendarIcon className="w-10 h-10" />
                <TextInput
                  id="to-date"
                  aria-label={t("common.to") as string}
                  type="date"
                  value={localToDate || toDate}
                  min={localFromDate || fromDate || undefined}
                  max={maximumToDate}
                  onChange={e => setLocalToDate(e.target.value)}
                />
              </div>
            </div>
            <div className="flex-none">
              <button
                type="button"
                className="btn-secondary w-full whitespace-nowrap"
                disabled={isFetching}
                onClick={applyDates}
              >
                {t("common.apply_filter")}
              </button>
            </div>
            {selectedLevels.includes("allocation_announcements") && (
              <div className="flex min-w-[18rem] flex-1 flex-wrap items-center gap-4">
                {(["as_at", "in_range"] as const).map(mode => (
                  <label
                    key={mode}
                    className="flex items-center gap-2 text-sm"
                    htmlFor={`announcements-${mode}`}
                  >
                    <input
                      id={`announcements-${mode}`}
                      type="radio"
                      name="announced-allocations"
                      checked={announcedAllocations === mode}
                      onChange={() => setAnnouncedAllocations(mode)}
                    />
                    {t(`reporting.announcements_${mode}`)}
                  </label>
                ))}
              </div>
            )}
          </fieldset>
        }
        contextActions={
          isTemplateRun && filters.length === 0 ? undefined : (
            <div className="flex flex-wrap items-center gap-4">
              {isTemplateRun ? (
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="font-medium text-gray-500">
                    {t("reporting.summary_filters")} ({filters.length}):
                  </span>
                  {filters.map((filter, index) => (
                    <span
                      key={index}
                      className="rounded border border-gray-200 bg-white px-3 py-1"
                    >
                      <span className="font-medium text-gray-500">
                        {t(
                          fieldByName.get(filter.field)?.label ?? filter.field,
                        )}
                      </span>{" "}
                      {t(
                        (fieldByName.get(filter.field)?.dataType === "date"
                          ? DATE_OPERATOR_LABEL_KEYS[filter.operator]
                          : undefined) ?? OPERATOR_LABEL_KEYS[filter.operator],
                      )}{" "}
                      {formatReportFilterValue(
                        filter.value,
                        fieldByName.get(filter.field)?.dataType,
                      )}
                    </span>
                  ))}
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-bold text-primary-3"
                    onClick={editColumns}
                  >
                    <EditIcon /> {t("reporting.edit_report_columns")}
                    {selectedFields.length > 0 ? (
                      <span className="ml-1 rounded-full bg-primary-1 px-2 py-0.5 text-xs text-white">
                        {selectedFields.length}
                      </span>
                    ) : null}
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-sm font-bold text-primary-3"
                    onClick={() => goToStep(1)}
                  >
                    <EditIcon /> {t("reporting.edit_report_filters")}
                    {filters.length > 0 ? (
                      <span className="ml-1 rounded-full bg-primary-1 px-2 py-0.5 text-xs text-white">
                        {filters.length}
                      </span>
                    ) : null}
                  </button>
                </>
              )}
            </div>
          )
        }
        actions={
          canGenerate ? (
            <>
              {!isTemplateRun ? (
                <button
                  type="button"
                  className="btn-outline-primary"
                  disabled={
                    createTemplate.isLoading || updateTemplate.isLoading
                  }
                  onClick={() => {
                    setSaveName(templateName ?? "");
                    setTemplateModalOpen(true);
                  }}
                >
                  {templateId
                    ? t("reporting.update_template")
                    : t("reporting.save_as_template")}
                </button>
              ) : null}
              <button
                type="button"
                className="btn-outline-primary"
                disabled={!result || isFetching || resultHidden}
                onClick={() => {
                  setSaveName(suggestedReportName);
                  setSnapshotModalOpen(true);
                }}
              >
                {t("reporting.save_snapshot")}
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={
                  !result ||
                  isFetching ||
                  isExporting ||
                  resultHidden ||
                  rows.length === 0
                }
                onClick={openExportDialog}
              >
                {t("reporting.export_data")}
              </button>
            </>
          ) : undefined
        }
        metadata={
          result ? (
            <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
              {!isAnnouncements ? <span>{resultScopeName ?? "-"}</span> : null}
              <span>
                {formatReportPeriodDate(result.dateRange.from)} -{" "}
                {formatReportPeriodDate(result.dateRange.to)}
              </span>
              {result.accountingPeriod ? (
                <span>
                  {t("reporting.water_year_summary", {
                    from: formatReportPeriodDate(
                      result.accountingPeriod.periodStart,
                    ),
                    to: formatReportPeriodDate(
                      result.accountingPeriod.periodEnd,
                    ),
                  })}
                </span>
              ) : null}
              <span>{t("reporting.total_rows", { total: result.total })}</span>
              <span>
                {t("reporting.selected_rows", {
                  count: selectedRecordIds.length,
                })}
              </span>
            </div>
          ) : undefined
        }
        columns={result?.columns ?? []}
        rows={rows}
        onSelectionChange={handleSelectionChange}
        tableKey={result?.runId}
        loading={
          isFetching ||
          (schemesLoading && !metadataError) ||
          (!scopeInitialised &&
            !metadataError &&
            !needsScheme &&
            !needsAccountingPeriod)
        }
        pageSize={PAGE_SIZE}
        tableMessage={
          metadataError ? (
            <div className="text-sm text-red-700">
              <p>{t("reporting.scope_load_failed")}</p>
              <button
                type="button"
                className="btn-default mt-3"
                onClick={() => refetchSchemes()}
              >
                {t("common.refresh")}
              </button>
            </div>
          ) : runError ? (
            <div className="text-sm text-red-700">
              <p>
                {(runFailure as any)?.response?.data?.message ??
                  t("reporting.run_failed")}
              </p>
              <button
                type="button"
                className="btn-default mt-3"
                onClick={() => refetchRun()}
              >
                {t("common.refresh")}
              </button>
            </div>
          ) : resultHidden ? (
            <p className="text-gray-500">
              {t(
                needsScheme
                  ? "reporting.select_scheme_to_run"
                  : "reporting.select_accounting_period_to_run",
              )}
            </p>
          ) : undefined
        }
        footer={
          <StepFooter
            onBack={isTemplateRun ? undefined : () => goToStep(1)}
            cancelLabel={
              isTemplateRun ? (t("common.back") as string) : undefined
            }
            cancelTo={isTemplateRun ? "/polestar/reports/adhoc#0" : undefined}
          />
        }
      />

      {exportDialog}

      <Modal open={templateModalOpen || snapshotModalOpen}>
        <div className="flex flex-col">
          <div className="border-b p-4">
            <h3 className="text-lg font-medium">
              {templateModalOpen
                ? templateId
                  ? t("reporting.update_template")
                  : t("reporting.save_as_template")
                : t("reporting.save_snapshot")}
            </h3>
          </div>
          <form
            onSubmit={event => {
              event.preventDefault();
              if (!saveName.trim()) return;
              if (templateModalOpen) handleSaveTemplate();
              else handleSaveSnapshot();
            }}
          >
            <div className="p-4 space-y-3">
              {snapshotModalOpen && (
                <p className="text-sm text-gray-500">
                  {t("reporting.snapshot_hint")}
                </p>
              )}
              <Label htmlFor="save-name">{t("common.name")}</Label>
              <TextInput
                id="save-name"
                value={saveName}
                maxLength={SAVED_REPORT_NAME_MAX_LENGTH}
                onChange={e => setSaveName(e.target.value)}
              />
            </div>
            <div className="border-t px-4 py-2 flex flex-row-reverse gap-3">
              <button
                type="submit"
                className="btn-primary"
                disabled={
                  !saveName.trim() ||
                  createTemplate.isLoading ||
                  updateTemplate.isLoading ||
                  createSnapshot.isLoading
                }
              >
                {t("common.save")}
              </button>
              <button
                type="button"
                className="btn-default"
                onClick={() => {
                  setTemplateModalOpen(false);
                  setSnapshotModalOpen(false);
                }}
              >
                {t("common.cancel")}
              </button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default StepPreview;
